import { gql } from "@apollo/client";
import { EntryFragment } from "queries/entry";
import { CupboardFragment } from "queries/cupboard";

// One chat message judged by the model: food to log, not food, or unsure, and
// the meal it most likely belongs to. Saves nothing; the chat itself uses
// `send` below.
export const classify = gql`
  mutation ChatClassify($input: ChatClassifyInput!) {
    viewer {
      account {
        chat {
          id
          classify(input: $input) {
            id
            kind
            probability
            meal
            mealConfidence
            food
            text
          }
        }
      }
    }
  }
`;

// One kept message of the day's thread: the person's text, or what the bot
// did, for components/chat/replies to word.
export const MessageFragment = gql`
  fragment ChatMessageFragment on ChatMessage {
    id
    messageId
    date
    from
    text
    kind
    meal
    entries {
      entryId
      action
      meal
      food
      serving
      calories
    }
    items {
      itemId
      action
      name
      quantity
    }
    note
    advice {
      status
      left
      needsFood
      flags {
        macro
        level
        text
      }
      options {
        label
        food
        kcal
        protein
        carbs
        fat
        fiber
      }
      buy
      answer
    }
    asking
    answered
    createdAt
  }
`;

// The day's thread, oldest first. Each day starts empty.
export const day = gql`
  query ChatDay($date: String!) {
    viewer {
      id
      account {
        id
        chat {
          id
          day(date: $date) {
            id
            date
            messages {
              ...ChatMessageFragment
            }
          }
        }
      }
    }
  }
  ${MessageFragment}
`;

// The chat's one call per message: kept, decided, and logged or applied. The
// entries are what was saved; `cupboard` is the cupboard after a message that
// changed it, which lands on the cached one by its id; `message` is the reply
// as the thread keeps it.
export const send = gql`
  mutation ChatSend($input: ChatSendInput!) {
    viewer {
      account {
        chat {
          id
          send(input: $input) {
            id
            kind
            probability
            meal
            text
            entries {
              ...EntryFragment
            }
            cupboard {
              ...CupboardFragment
            }
            message {
              ...ChatMessageFragment
            }
          }
        }
      }
    }
  }
  ${EntryFragment}
  ${CupboardFragment}
  ${MessageFragment}
`;

// The yes or no to "is this something you ate?", by the reply that asked.
export const answer = gql`
  mutation ChatAnswer($input: ChatAnswerInput!) {
    viewer {
      account {
        chat {
          id
          answer(input: $input) {
            id
            kind
            meal
            text
            entries {
              ...EntryFragment
            }
            cupboard {
              ...CupboardFragment
            }
            message {
              ...ChatMessageFragment
            }
          }
        }
      }
    }
  }
  ${EntryFragment}
  ${CupboardFragment}
  ${MessageFragment}
`;

// Logged without asking whether it is food, and outside the thread. The chat
// itself answers through `answer` above.
export const log = gql`
  mutation ChatLog($input: ChatLogInput!) {
    viewer {
      account {
        chat {
          id
          log(input: $input) {
            id
            kind
            probability
            meal
            text
            entries {
              ...EntryFragment
            }
          }
        }
      }
    }
  }
  ${EntryFragment}
`;
